import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listExchangeRuns, triggerInbound, triggerOutbound } from '@/api/exchangeRuns';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Table, { type Column } from '@/components/ui/Table';
import type { ExchangeRun } from '@/types';
import { useState } from 'react';

function outcomeBadge(outcome: string) {
  if (outcome === 'SUCCESS') return <Badge variant="green">Success</Badge>;
  if (outcome === 'PARTIAL') return <Badge variant="amber">Partial</Badge>;
  return <Badge variant="red">Failed</Badge>;
}

export default function ExchangeRunsPage() {
  const qc = useQueryClient();
  const [triggerError, setTriggerError] = useState('');

  const { data: runs, isLoading } = useQuery({
    queryKey: ['exchange-runs'],
    queryFn: listExchangeRuns,
    refetchInterval: 10000, // poll every 10s
  });

  const inboundMutation = useMutation({
    mutationFn: triggerInbound,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exchange-runs'] }),
    onError: (err: unknown) => {
      setTriggerError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Trigger failed',
      );
    },
  });

  const outboundMutation = useMutation({
    mutationFn: triggerOutbound,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['exchange-runs'] }),
    onError: (err: unknown) => {
      setTriggerError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Trigger failed',
      );
    },
  });

  const columns: Column<ExchangeRun>[] = [
    {
      key: 'direction',
      header: 'Direction',
      render: (r) => (
        <Badge variant={r.direction === 'INBOUND' ? 'blue' : 'amber'}>
          {r.direction}
        </Badge>
      ),
    },
    {
      key: 'periods',
      header: 'Periods',
      render: (r) => <span className="text-sm text-gray-700">{r.periods.join(', ')}</span>,
    },
    {
      key: 'rowCount',
      header: 'Rows',
      render: (r) => <span className="font-mono text-gray-700">{r.rowCount}</span>,
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (r) => outcomeBadge(r.outcome),
    },
    {
      key: 'triggeredBy',
      header: 'Triggered by',
      render: (r) => (
        <span className="text-xs text-gray-500">{r.triggeredBy ?? 'Scheduled'}</span>
      ),
    },
    {
      key: 'startedAt',
      header: 'Started',
      sortable: true,
      render: (r) => (
        <span className="text-xs text-gray-500">
          {new Date(r.startedAt).toLocaleString('en-GB')}
        </span>
      ),
    },
    {
      key: 'completedAt',
      header: 'Completed',
      render: (r) => (
        <span className="text-xs text-gray-500">
          {r.completedAt
            ? new Date(r.completedAt).toLocaleString('en-GB')
            : 'Running…'}
        </span>
      ),
    },
    {
      key: 'error',
      header: 'Error',
      render: (r) =>
        r.error ? (
          <span className="text-xs text-red-600 truncate max-w-[200px] block" title={r.error}>
            {r.error}
          </span>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exchange Runs</h1>
          <p className="mt-1 text-sm text-gray-500">
            Qlik CSV sync history. Inbound: Qlik → Cashflow. Outbound: Cashflow → Qlik.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            loading={inboundMutation.isPending}
            onClick={() => { setTriggerError(''); inboundMutation.mutate(); }}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Inbound Sync
          </Button>
          <Button
            size="sm"
            loading={outboundMutation.isPending}
            onClick={() => { setTriggerError(''); outboundMutation.mutate(); }}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            Outbound Sync
          </Button>
        </div>
      </div>

      {triggerError && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {triggerError}
        </div>
      )}

      <Table
        columns={columns}
        data={runs ?? []}
        loading={isLoading}
        rowKey={(r) => r.id}
        emptyMessage="No exchange runs yet."
      />
    </div>
  );
}
